import SumsubWebSdk from "@sumsub/websdk-react";

export interface ProviderCaptureSdkProps {
  /** Ephemeral access token from `capture/start` — mounts the embedded capture widget. */
  accessToken: string;
  /** Called when the token expires; must resolve to a fresh access token. */
  onExpired: () => Promise<string>;
  /** Fired when the applicant finishes the capture (the final verdict still comes via the webhook). */
  onSubmitted: () => void;
  /** Fired on a fatal SDK error. */
  onSdkError: () => void;
  /** UI language for the embedded widget (`es` | `en` | `pt`). */
  lang?: string;
}

// Message types that mean the user finished uploading/submitting. The final
// verdict is NOT decided here — it arrives asynchronously via the webhook and is
// surfaced by status polling / the socket. We only use these to advance to the
// "processing" screen.
const SUBMITTED_MESSAGE_TYPES = new Set([
  "idCheck.onApplicantSubmitted",
  "idCheck.applicantSubmitted",
  "idCheck.onApplicantResubmitted",
]);

/**
 * Mounts the embedded verification capture widget inside the branded `ProviderCaptureFrame` slot using the
 * ephemeral access token from `capture/start`. The capture (and all PII) stays with the provider; Coloca
 * never receives the raw images. Provider branding is configured provider-side; no provider name is rendered
 * here.
 */
export function ProviderCaptureSdk({
  accessToken,
  onExpired,
  onSubmitted,
  onSdkError,
  lang = "es",
}: ProviderCaptureSdkProps) {
  return (
    <div className="h-full w-full">
      <SumsubWebSdk
        accessToken={accessToken}
        expirationHandler={onExpired}
        config={{ lang }}
        options={{ addViewportTag: false, adaptIframeHeight: true }}
        onMessage={(type) => {
          if (SUBMITTED_MESSAGE_TYPES.has(type)) onSubmitted();
        }}
        onError={() => onSdkError()}
      />
    </div>
  );
}
